import { Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { closeModal } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { handleSubmitError } from '@utils/forms';

import {
  CreateGenderRequest,
  Gender,
  UpdateGenderRequest,
} from '@interfaces/genders.interface';

import {
  useCreateGenderMutation,
  useUpdateGenderMutation,
} from '@api/genders.api';

import useFormEnhanced from '@hooks/useFormEnhanced';

export const UPSERT_GENDER_FORM_MODAL_ID = 'upsertGender';

interface UpsertGenderFormProps {
  gender?: Gender;
}

export default function UpsertGenderForm({ gender }: UpsertGenderFormProps) {
  const initialValues = {
    name: gender?.name || '',
  };

  const form = useFormEnhanced({
    initialValues,
    normalizeFields: {
      capitalizeFields: ['name'],
    },
  });

  const [createGender, { isLoading: isLoadingCreate }] =
    useCreateGenderMutation();
  const [updateGender, { isLoading: isLoadingUpdate }] =
    useUpdateGenderMutation();

  const onCancelClick = () => {
    closeModal(UPSERT_GENDER_FORM_MODAL_ID);
  };

  const onSubmit = async (values: typeof initialValues) => {
    try {
      if (gender) {
        const data: UpdateGenderRequest = {
          id: gender.id,
          body: {
            name: values.name,
          },
        };

        await updateGender(data).unwrap();

        notifications.show({
          title: 'Genere modificato',
          message: 'Genere modificato con successo.',
        });
      } else {
        const data: CreateGenderRequest = {
          name: values.name,
        };

        await createGender(data).unwrap();

        notifications.show({
          title: 'Genere creato',
          message: 'Genere creato con successo.',
        });
      }

      closeModal(UPSERT_GENDER_FORM_MODAL_ID);
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm">
        <TextInput
          data-autofocus
          required
          label="Nome"
          {...form.getInputProps('name')}
        />
      </Stack>

      {form.errors.general && (
        <Text c="red" size="xs">
          {form.errors.general}
        </Text>
      )}

      <Group justify="right" mt="xl">
        <Button
          onClick={onCancelClick}
          variant="default"
          loading={isLoadingCreate || isLoadingUpdate}
        >
          Annulla
        </Button>
        <Button type="submit" loading={isLoadingCreate || isLoadingUpdate}>
          Conferma
        </Button>
      </Group>
    </form>
  );
}
